import * as React from 'react'
import { Loader2 } from 'lucide-react'
import type {
  ProposalDetailDto,
  ProposalSiteRefDto,
  ProposalVersionDto,
} from '@lib/dto/proposal.dto'
import type { ProposalWork } from '@lib/enums/proposal-work.enum'
import { useSwitchToNewestVisits } from '@/hooks/useProposalMutations'
import { toErrorMessage } from '@/helpers/logger'
import { formatVisitDay } from '@/site-visits/VisitBar'
import { Button } from '@/components/ui/button'

/** One kind of visit a draft is behind on. */
interface Stale {
  siteId: string
  name: string
  /** Named only when the proposal bids both kinds of work. */
  kind: 'trees' | 'irrigation' | null
  builtFrom: string
  newest: string
}

/**
 * Every newer visit a draft is behind on.
 *
 * A site that bids both trees and irrigation can be behind on each. Stopping
 * at the tree pair used to hide the irrigation dates.
 */
export function staleVisitNotices(
  work: ProposalWork,
  sites: readonly ProposalSiteRefDto[],
): Stale[] {
  const trees = work !== 'IRRIGATION'
  const irrigation = work !== 'TREES'
  const nameKind = trees && irrigation
  return sites.flatMap((site) => {
    const notices: Stale[] = []
    if (trees && site.treeVisit != null && site.newerTreeVisit != null) {
      notices.push({
        siteId: site.siteId,
        name: site.name,
        kind: nameKind ? 'trees' : null,
        builtFrom: site.treeVisit.startedOn,
        newest: site.newerTreeVisit.startedOn,
      })
    }
    if (
      irrigation &&
      site.irrigationVisit != null &&
      site.newerIrrigationVisit != null
    ) {
      notices.push({
        siteId: site.siteId,
        name: site.name,
        kind: nameKind ? 'irrigation' : null,
        builtFrom: site.irrigationVisit.startedOn,
        newest: site.newerIrrigationVisit.startedOn,
      })
    }
    return notices
  })
}

/**
 * A draft built from a visit that a newer one has replaced, with the choice
 * to rebuild it from the newest.
 *
 * Nothing moves on its own (the operator's call, 2026-10-16): a draft keeps
 * showing what it was built from, so a site visited again does not change a
 * bid somebody is halfway through, and this says so rather than letting the
 * old findings pass for this year's.
 */
export function NewerVisitNotice({
  proposal,
  version,
}: {
  proposal: ProposalDetailDto
  version: ProposalVersionDto
}) {
  const switchVisits = useSwitchToNewestVisits()
  const [error, setError] = React.useState<string | null>(null)
  const stale = staleVisitNotices(proposal.work, version.sites)
  if (stale.length === 0) return null

  async function handleSwitch(): Promise<void> {
    setError(null)
    try {
      await switchVisits.mutateAsync({
        versionId: version.id,
        proposalId: proposal.id,
      })
    } catch (caught) {
      setError(toErrorMessage(caught))
    }
  }

  const only = stale.length === 1 ? stale[0] : undefined
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-default bg-warning-subtle px-4 py-2 text-sm">
      <div className="min-w-0 flex-1 space-y-0.5">
        {only !== undefined ? (
          <p>
            Built from the visit of {formatVisitDay(only.builtFrom)}. A newer
            visit exists ({formatVisitDay(only.newest)}).
          </p>
        ) : (
          <>
            <p>Some sites have a newer visit than this draft is built from:</p>
            <ul className="list-disc pl-5 text-xs text-secondary-text">
              {stale.map((site) => (
                <li key={`${site.siteId}:${site.kind ?? 'visit'}`}>
                  {site.name}
                  {site.kind === null ? '' : ` (${site.kind})`}: built from{' '}
                  {formatVisitDay(site.builtFrom)}, newest{' '}
                  {formatVisitDay(site.newest)}
                </li>
              ))}
            </ul>
          </>
        )}
        {error === null ? null : (
          <p className="text-xs text-destructive">{error}</p>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={switchVisits.isPending}
        onClick={() => {
          void handleSwitch()
        }}
      >
        {switchVisits.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : null}
        Use the newest
      </Button>
    </div>
  )
}
